import { getAuthToken } from '../lib/authToken';
const API_BASE_URL = '/api/users';

export interface CreateUserData {
  firstName: string;
  lastName: string;
  email: string;
  role: 'admin' | 'lawyer' | 'paralegal' | 'assistant';
  permissions: string[];
  phone?: string;
}

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: 'admin' | 'lawyer' | 'paralegal' | 'assistant';
  isActive: boolean;
  isFirstLogin: boolean;
  phone?: string;
  permissions: string[];
  lastLoginAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface UsersResponse {
  success: boolean;
  data: {
    users: User[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
}

export interface CreateUserResponse {
  success: boolean;
  message: string;
  data: User & {
    temporaryPassword?: string;
    emailSent?: boolean;
  };
}

export interface ResetPasswordResponse {
  success: boolean;
  message: string;
  data: {
    id: string;
    email: string;
    temporaryPassword?: string;
    emailSent?: boolean;
  };
}

class UserService {
  private getAuthHeaders() {
    return {
      'Authorization': `Bearer ${getAuthToken()}`,
      'Content-Type': 'application/json',
    };
  }

  // Get all users
  async getUsers(params?: {
    page?: number;
    limit?: number;
    search?: string;
    role?: string;
    status?: string;
  }): Promise<UsersResponse> {
    const searchParams = new URLSearchParams();
    
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    if (params?.role) searchParams.append('role', params.role);
    if (params?.status) searchParams.append('status', params.status);

    const response = await fetch(`${API_BASE_URL}?${searchParams.toString()}`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch users');
    }

    const data = await response.json();
    return data;
  }

  // Create a new user
  async createUser(userData: CreateUserData): Promise<CreateUserResponse> {
    const response = await fetch(API_BASE_URL, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      throw new Error('Failed to create user');
    }

    const data = await response.json();
    return data;
  }

  // Update an existing user
  async updateUser(userId: string, userData: Partial<CreateUserData>): Promise<CreateUserResponse> {
    const response = await fetch(`${API_BASE_URL}/${userId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      throw new Error('Failed to update user');
    }

    const data = await response.json();
    return data;
  }

  // Delete a user
  async deleteUser(userId: string): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${API_BASE_URL}/${userId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to delete user');
    }

    const data = await response.json();
    return data;
  }

  // Reset user password
  async resetUserPassword(userId: string): Promise<ResetPasswordResponse> {
    const response = await fetch(`${API_BASE_URL}/${userId}/reset-password`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to reset user password');
    }

    const data = await response.json();
    return data;
  }

  // Suspend or activate a user
  async suspendUser(userId: string, isActive: boolean): Promise<{ success: boolean; message: string; data: any }> {
    const response = await fetch(`${API_BASE_URL}/${userId}/suspend`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ isActive }),
    });

    if (!response.ok) {
      throw new Error('Failed to suspend/activate user');
    }

    const data = await response.json();
    return data;
  }

  // Get user by ID
  async getUserById(userId: string): Promise<{ success: boolean; data: User }> {
    const response = await fetch(`${API_BASE_URL}/${userId}`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch user');
    }

    const data = await response.json();
    return data;
  }
}

export const userService = new UserService(); 